
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL_CMS_SERVICE } from "@/api/constant";
import CommonFilter from "@/components/common/CommonFilter";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import ImagePreview from "@/components/common/media/ImagePreview";
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from "@/components/common/PageHeading";
import Layout from "@/components/Layout/Layout"
import Card from "@/components/UI/Card";
import { getPermissionName } from "@/context/permission";
import { usePermissions } from "@/hooks/usePermissions";
import { ActionConfig, PageInfo } from "@/interfaces/common.interface";
import { IContentData, IContentResponse } from "@/interfaces/contentResponse.interface";
import { ContentKey } from "@/utility/enum";
import { GetFilterUrl } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface StatesProp {
    isLoading: boolean
    contents: IContentData[],
    pageInfo: PageInfo
    showDeleteModel: boolean
    contentToDelete: string | null
    contentKey: string | null
}

const WebstieContent = () => {
    const navigate = useNavigate();
    const { canCreate, canEdit, canView, canDelete } = usePermissions();
    const [states, setStates] = useState<StatesProp>({
        isLoading: false,
        contents: [],
        pageInfo: {
            total: 0,
            totalPages: 1,
            limit: 10,
            page: 1,
        },
        showDeleteModel: false,
        contentToDelete: null,
        contentKey: null
    });

    const handleChangeState = (newState: Partial<typeof states>) => {
        setStates((prev) => ({
            ...prev,
            ...newState,
        }));
    };


    const fetchContents = async () => {
        try {
            const filterQuery = {
                page: states.pageInfo.page,
                limit: states.pageInfo.limit,
                contentKey: states.contentKey
            };
            const query = GetFilterUrl(filterQuery)
            handleChangeState({ isLoading: true })
            const PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.content}?${query ? query : ''}`;
            const res = await callApi<IContentResponse>(PATH, { method: 'GET' });
            handleChangeState({ isLoading: false, contents: res.data })
        } catch (error) {
            handleChangeState({ isLoading: false })
            ThrowError(error)
        }
    };

    useEffect(() => {
        fetchContents();
    }, [states.pageInfo.page, states.pageInfo.limit, states.contentKey]);

    const handleDeleteContent = async (id: string) => {
        try {
            const PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.content}/${id}`
            await callApi(PATH, { method: 'DELETE' });
            fetchContents();
        } catch (error) {
            ThrowError(error)
        }
    };

    const contentActionConfig: Record<string, ActionConfig<IContentData>> = {
        eidt: {
            text: 'Edit',
            onClick: (content) =>
                navigate(`${RoutesLink.addContent}/${content.id}`),
            permission: () => canEdit('content'),

        },
        delete: {
            text: 'Delete',
            onClick: (content) => {
                handleChangeState({ showDeleteModel: true, contentToDelete: content.id })
            },
            permission: () => canDelete('product'),
        },
    }
    const actions = useMemo(
        () =>
            Object.values(contentActionConfig)
                .filter(action => action.permission())
                .map(({ permission, ...rest }) => rest),
        [canView, canEdit, canDelete]
    );

    const columns: TableColumn<IContentData>[] = [
        {
            key: 'Media',
            header: 'Media',
            render: (item) => {
                return (
                    <ImagePreview
                        type={item.contentType as 'image' | 'video'}
                        src={`${API_URL_CMS_SERVICE}/${item?.valueUrl}`}
                        alt={''}
                    />
                )
            },
            className: 'p-3',
        },
        {
            key: 'valueText',
            header: 'Title'
        },
        {
            key: 'contentKey',
            header: 'Key'
        },
        {
            key: 'contentType',
            header: 'Content Type'
        },

    ]

    const contentkeys = [
        { label: 'All', value: '' },
        { label: 'Front Banner', value: ContentKey.LOGIN_BACKGROUND },
        { label: 'Home Banner', value: ContentKey.HOME_PAGE_BANNER },
        { label: 'Pop Up', value: ContentKey.POP_UP },
    ]


    return (
        <Layout>
            <PageHeading
                text="Content"
                button={
                    canCreate(getPermissionName('add:website content')) ?
                        {
                            icon: <Plus size={20} />,
                            text: 'Add Content',
                            onClick: () => navigate(RoutesLink.addContent)

                        } : undefined}
            />
            <Card>
                <CommonFilter
                    filters={contentkeys}
                    showSearch={false}
                    onChange={(val) => handleChangeState({ contentKey: val })}
                />
                <CommonTable
                    showCheckbox={false}
                    columns={columns}
                    data={states.contents}
                    actions={actions}
                    loading={states.isLoading}
                />
            </Card>
            {
                states.showDeleteModel && (
                    <ConfirmationModel
                        show={states.showDeleteModel}
                        onClose={() => { handleChangeState({ showDeleteModel: false, contentToDelete: null }) }}
                        onConfirm={() => handleDeleteContent(states.contentToDelete as string)}
                        type="content"
                    />
                )
            }
        </Layout>
    )
}

export default WebstieContent;